<template>
  <section class="admin-content" id="call-centre">
    <Navbar />
    <main class="admin-main">
      <div class="page-header">
        <div class="container">
          <div class="d-flex align-items-center text-white" style="padding: 32px 0;">
            <div class="header-icon-wrap mr-3"><i class="fe fe-phone-call" style="font-size: 22px;"></i></div>
            <div>
              <h4 class="mb-0 font-weight-bold">Call Centre</h4>
              <small style="opacity: 0.8;">Enrollees to call about renewing their cover; a task closes itself when the enrollee renews</small>
            </div>
          </div>
        </div>
      </div>
      <section style="margin-top: -20px;">
        <div class="container pb-5">
          <div class="row" v-if="summary">
            <div class="col-md-3 col-6" v-for="tile in tiles" :key="tile.label">
              <div class="stat-card">
                <div class="stat-icon-wrap" :style="{ background: tile.bg }"><i :class="tile.icon" :style="{ color: tile.color }"></i></div>
                <div><p class="stat-label">{{ tile.label }}</p><h3 class="stat-value">{{ tile.value }}</h3></div>
              </div>
            </div>
          </div>

          <RenewalsPanel />

          <div class="card shadow-sm table-card">
            <div class="form-card-header flex-wrap">
              <div class="section-number"><i class="fe fe-list" style="font-size: 12px;"></i></div>
              <h6 class="mb-0 font-weight-bold text-white flex-grow-1">Call list</h6>
              <select class="form-control form-control-sm" style="width: auto;" v-model="status" @change="load(1)">
                <option value="open">To call</option><option value="">All</option>
                <option v-for="s in statuses" :key="s" :value="s">{{ statusLabel(s) }}</option>
              </select>
              <label class="text-white small mb-0 ml-2"><input type="checkbox" v-model="mine" @change="load(1)" /> Mine</label>
              <input class="form-control form-control-sm ml-2" style="max-width: 200px;" placeholder="Name or ID…" v-model="search" @keyup.enter="load(1)" />
            </div>
            <div class="table-responsive">
              <table class="table mb-0">
                <thead class="table-head">
                  <tr><th class="table-th">Enrollee</th><th class="table-th">Phone</th><th class="table-th">Programme</th><th class="table-th">Cover ends</th><th class="table-th">Status</th><th class="table-th">Notes</th><th class="table-th"></th></tr>
                </thead>
                <tbody>
                  <tr v-if="!tasks.length"><td colspan="7" class="empty-state">Nothing to call. Use "Send to call centre" above to add enrollees.</td></tr>
                  <tr class="table-row" v-for="t in tasks" :key="t.id">
                    <td><router-link :to="`/client/${t.user_id}`">{{ t.firstname }} {{ t.lastname }}</router-link>
                      <div class="small text-muted">{{ t.id_card_number }} · {{ t.facility || "no facility" }}</div></td>
                    <td><a :href="`tel:${t.phone}`">{{ t.phone }}</a></td>
                    <td>{{ t.sector || "—" }}</td>
                    <td>{{ t.expiry_date }}<div class="small" :class="t.reason === 'expired' ? 'text-danger' : 'text-muted'">{{ t.reason === "expired" ? "expired" : "due" }}</div></td>
                    <td><span class="sev" :class="statusClass(t.status)">{{ statusLabel(t.status) }}</span>
                      <div class="small text-muted">{{ t.attempts }} attempt(s)<span v-if="t.assigned_name"> · {{ t.assigned_name }}</span></div></td>
                    <td style="min-width: 200px;"><textarea class="form-control form-control-sm" rows="1" v-model="t.notes" @change="update(t, null)" placeholder="Notes…"></textarea></td>
                    <td class="text-nowrap">
                      <select class="form-control form-control-sm" :value="''" @change="update(t, $event.target.value); $event.target.value = ''">
                        <option value="">Record call…</option>
                        <option value="called">Called, will renew</option>
                        <option value="reached">Reached, done</option>
                        <option value="not_reachable">Not reachable</option>
                        <option value="declined">Declined</option>
                        <option value="open">Reopen</option>
                      </select>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="card-footer bg-white d-flex justify-content-between align-items-center" v-if="meta.last_page > 1">
              <small class="text-muted">Page {{ meta.current_page }} of {{ meta.last_page }} · {{ meta.total }} tasks</small>
              <div>
                <button class="btn btn-sm btn-outline-secondary mr-1" :disabled="meta.current_page <= 1" @click="load(meta.current_page - 1)">Previous</button>
                <button class="btn btn-sm btn-outline-secondary" :disabled="meta.current_page >= meta.last_page" @click="load(meta.current_page + 1)">Next</button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </section>
</template>

<script>
import Navbar from "@/views/Navbar.vue";
import RenewalsPanel from "./components/RenewalsPanel.vue";

/** Call centre worklist (GET/PUT call-centre/tasks) with the renewals panel to push more. */
export default {
  components: { Navbar, RenewalsPanel },
  data() {
    return {
      status: "open", mine: false, search: "", tasks: [], summary: null,
      meta: { current_page: 1, last_page: 1, total: 0 },
      statuses: ["open", "called", "reached", "not_reachable", "declined", "renewed"],
    };
  },
  computed: {
    tiles() {
      const s = this.summary;
      return [
        { label: "To call", value: s.open, icon: "fe fe-phone", bg: "#fff7ed", color: "#ea580c" },
        { label: "Renewed", value: s.by_status.renewed, icon: "fe fe-check-circle", bg: "#ecfdf5", color: "#059669" },
        { label: "Not reachable", value: s.by_status.not_reachable, icon: "fe fe-phone-off", bg: "#fef2f2", color: "#dc2626" },
        { label: "Converted", value: s.conversion_pct === null ? "—" : s.conversion_pct + "%", icon: "fe fe-trending-up", bg: "#eff6ff", color: "#2563eb" },
      ];
    },
  },
  mounted() { this.load(1); },
  methods: {
    load(page) {
      this.axios.get("/api/v1/auth/call-centre/tasks", { params: { page, status: this.status || undefined, mine: this.mine ? 1 : undefined, search: this.search || undefined } })
        .then((res) => {
          this.tasks = res.data.data;
          this.summary = res.data.summary;
          this.meta = { current_page: res.data.current_page, last_page: res.data.last_page, total: res.data.total };
        });
    },
    update(t, status) {
      this.axios.put(`/api/v1/auth/call-centre/tasks/${t.id}`, { status: status || undefined, notes: t.notes })
        .then((res) => { Object.assign(t, res.data.data); if (status) this.load(this.meta.current_page); })
        .catch(() => this.$toasted.error("Could not save.", { position: "top-center", duration: 3000 }));
    },
    statusLabel(s) {
      return { open: "To call", called: "Called", reached: "Reached", not_reachable: "Not reachable", declined: "Declined", renewed: "Renewed" }[s] || s;
    },
    statusClass(s) {
      return { open: "sev-medium", called: "sev-low", reached: "sev-low", not_reachable: "sev-high", declined: "sev-info", renewed: "sev-low" }[s];
    },
  },
};
</script>

<style scoped>
@import "../reports/reports.css";
</style>
